import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Formik, Form, FormikProps, FormikHelpers } from 'formik';
import merge from 'lodash.merge';

import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useTheme } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import Container from '@material-ui/core/Container';

import compose from 'helpers/compose';
import { sortCountries, globalErrToForm } from 'helpers/helpers';
import ControlButtons from 'components/ControlButtons';
import Switch from 'components/FormFields/Switch';
import Background from 'components/Layout/Background';
import FormTextField from 'components/FormFields/FormTextField';
import FormSelectField from 'components/FormFields/FormSelectField';
import DatePicker from 'components/FormFields/FormDatePicker';
import FormRadioGroup from 'components/FormRadioGroup';
import EditFormModal from 'components/Modals/EditFormModal/EditFormModal';
import FormPhoneField from 'components/FormFields/FormPhoneField';
import Info from 'components/Tooltip/Info';
import ClientSearch from 'components/ClientSearch/ClientSearch';

import { RootStore } from 'stores/rootStore';
import { Params } from 'pages/Accounts/Edit/accountEditRoutes';
import { WorkflowPersonalDataStore } from 'stores/workflowStores/workflowPersonalDataStore/workflowPersonalDataStore';
import {
  ContractPhysicalData,
  ContractLegalData,
  initialValuesPhysical,
  initialValuesLegal,
} from 'stores/workflowStores/workflowPersonalDataStore/model';
import { PersonType, AccountMode } from 'enums';
import { ContractTab } from '../Contract';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Tooltip from 'components/Tooltip/Tooltip';
import BankAccounts from 'pages/Accounts/Edit/components/BankAccounts';
import Employers from 'pages/Accounts/Edit/components/Employers';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import Address from './components/Address';
import TaxResidency from './components/TaxResidency';
import PersonalId from './components/PersonalId';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Notification from './components/Notification';
import RequiredDocUpload from 'components/RequiredDocUpload/RequiredDocUpload';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { API_ACCOUNT_CONTRACT_PERSONAL_DATA_PERMIT, API_ACCOUNT_DETAIL_PERSONAL_DATA_PERMIT } from 'api/endpoints';

interface Props {
  rootStore: RootStore;
  tabChange: (tab: ContractTab) => void;
  //detailMode?: boolean;
  readOnly?: boolean;
  //mode?: AccountMode;
  detailStore?: AccountDetailStore;
}

const PersonalData: React.FC<Props> = ({ rootStore, tabChange, readOnly, detailStore }) => {
  const detailMode = !!detailStore;
  const { accountId } = useParams<Params>();
  const [store] = useState(new WorkflowPersonalDataStore(rootStore, accountId, detailMode));
  const { t, i18n } = useTranslation();
  const { palette } = useTheme();
  const { validatePassport, validateFromToDocument, editFormModal } = store;
  const [prefix, setPrefix] = useState<string | undefined>(undefined);
  const [legal, setLegal] = useState<boolean | undefined>(undefined);
  const detailUnlock = readOnly === false; // editing active account from detail; readOnly is set to false (undef. in flow)
  const mobile = useMediaQuery('(max-width:400px)', { noSsr: true });
  const mode = detailStore?.mode || rootStore.stores.editAccountStore.mode || undefined;

  //if (!detailMode) mode = rootStore.stores.editAccountStore.mode || undefined;

  useEffect(() => {
    rootStore.stores.editAccountStore.setStep(6);
  }, [rootStore.stores.editAccountStore]);

  useEffect(() => {
    (async () => {
      await store.fetchPersonalData();
      setPrefix(store.data !== null && store.data.personType === PersonType.LEGAL ? 'representative.' : '');
      setLegal(store.data !== null && store.data.personType === PersonType.LEGAL);
    })();
  }, [store, readOnly]);

  const handleSubmit = (
    values: ContractPhysicalData | ContractLegalData,
    formikHelpers: FormikHelpers<ContractPhysicalData | ContractLegalData>,
  ) => {
    store.handleNext(tabChange, values, formikHelpers, detailStore?.goFwd);
  };

  const permitNeeded = (values: ContractPhysicalData | ContractLegalData) => {
    return (
      ('representative' in values &&
        !['CZE', 'SVK'].includes(values.representative.citizenship) &&
        values.representative.permanentAddress.country === 'CZE') ||
      (!('representative' in values) &&
        !['CZE', 'SVK'].includes(values.citizenship) &&
        values.permanentAddress.country === 'CZE')
    );
  };

  const permitType = (values: ContractPhysicalData | ContractLegalData) => {
    return (
      ('representative' in values && values.representative.identificationDocument.type === 'RESIDENCE_PERMIT') ||
      (!('representative' in values) && values.identificationDocument.type === 'RESIDENCE_PERMIT')
    );
  };

  const allowIdTypes = (
    // FIXME: idType enum
    idType: string,
    values: ContractPhysicalData | ContractLegalData,
    formikHelpers: FormikHelpers<ContractPhysicalData | ContractLegalData>,
  ) => {
    if (permitNeeded(values)) {
      formikHelpers.setFieldValue(`${prefix}identificationDocument.type`, 'RESIDENCE_PERMIT');
    } else if (idType === 'RESIDENCE_PERMIT') {
      formikHelpers.setFieldValue(`${prefix}identificationDocument.type`, '');
    }
  };

  return store.data !== null && prefix !== undefined && legal !== undefined ? (
    <Container maxWidth="md">
      <Formik
        initialValues={merge(
          {},
          store.data.personType === PersonType.PHYSICAL ? initialValuesPhysical : initialValuesLegal,
          store.data,
        )}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            <Background afterMenu={!detailUnlock}>
              {detailMode && (
                <>
                  {!readOnly && (
                    <Background inside>
                      <Typography variant="h3">
                        {t('accountDetail_allowedEdits')}
                        <Info content={t('tooltip_accountDetail_allowedEdits')} />
                      </Typography>
                    </Background>
                  )}
                  <Background inside>
                    <FormTextField name={`title`} label={t('general_accountName')} readOnly={readOnly} fast={false} />
                  </Background>
                </>
              )}
              {legal && (
                <>
                  <Background inside>
                    <Typography variant="h3">{t('contract_basicData')}</Typography>

                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          name={`companyName`}
                          label={t('general_companyName')}
                          readOnly={readOnly || (!detailUnlock && !legal && formikProps.values.readOnly) || false}
                          fast={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          name={`companyNumber`}
                          label={t('general_companyNumber')}
                          readOnly={readOnly || formikProps.values.readOnly || detailUnlock || false}
                          fast={false}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          name={`legalEntityIdentifier`}
                          label={t('contract_legal_legalEntityIdentifier')}
                          readOnly={readOnly}
                          tooltipContent={
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t('contract_legal_lei_tooltip', {
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          }
                          fast={false}
                          maxLength={20}
                        />
                      </Grid>
                      <Grid item xs={10} sm={6}>
                        <DatePicker
                          maxDate={DateTime.local()}
                          minDate={DateTime.local(1900, 1, 1)}
                          name={`registrationDate`}
                          label={t('contract_legal_registrationDate')}
                          readOnly={readOnly || detailUnlock}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          name={`registrationPlace`}
                          label={t('contract_legal_registrationPlace')}
                          readOnly={readOnly || detailUnlock}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {readOnly || detailUnlock ? (
                          <FormTextField
                            name={`registrationCountry`}
                            label={t('contract_legal_registrationCountry')}
                            value={t(
                              `country_${
                                'registrationCountry' in formikProps.values
                                  ? formikProps.values.registrationCountry
                                  : ''
                              }`,
                            )}
                            readOnly
                          />
                        ) : (
                          <FormSelectField
                            name={`registrationCountry`}
                            label={t('contract_legal_registrationCountry')}
                            options={sortCountries(
                              formikProps.values.options.countryCode.map((code) => ({
                                value: code,
                                label: t(`country_${code}`),
                              })),
                            )}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormTextField
                          name={`email`}
                          label={t('general_email')}
                          readOnly={!detailUnlock}
                          fast={false}
                          endAdornment={
                            !detailUnlock && !readOnly ? (
                              <Tooltip title={t('tooltip_personalData_lockedInputAlt')}>
                                <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                              </Tooltip>
                            ) : undefined
                          }
                        />
                      </Grid>
                      <Grid item xs={8} sm={6}>
                        <FormTextField
                          name={`phone`}
                          label={t('general_phone')}
                          readOnly={!detailUnlock}
                          fast={false}
                          endAdornment={
                            !detailUnlock && !readOnly ? (
                              <Tooltip title={t('tooltip_personalData_lockedInputAlt')}>
                                <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                              </Tooltip>
                            ) : undefined
                          }
                        />
                      </Grid>
                    </Grid>
                  </Background>

                  <Background inside>
                    <Typography variant="h3">{t(`contract_permanentAddress${legal ? '_legal' : ''}`)}</Typography>
                    <Address
                      readOnly={readOnly}
                      prefix=""
                      addressType="permanentAddress"
                      countries={
                        legal
                          ? formikProps.values.options?.countryCode
                          : formikProps.values.options?.residencyBirthCountry
                      }
                      country={formikProps.values.permanentAddress.country}
                    />
                  </Background>

                  <Background inside>
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="h3">{t('contract_contactAddress')}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Switch
                          name={`contactAndPermanentAddressesAreSame`}
                          label={t(`contract_contactAddress_same_legal${mobile ? '_short' : ''}`)}
                          disabled={readOnly}
                        />
                      </Grid>
                    </Grid>
                    {formikProps.values.contactAndPermanentAddressesAreSame === false && (
                      <Address
                        readOnly={readOnly}
                        prefix=""
                        addressType="contactAddress"
                        countries={formikProps.values.options?.countryCode}
                        country={formikProps.values.contactAddress.country}
                      />
                    )}
                  </Background>

                  <Background compressed inside>
                    <Typography variant="h3">{t('contract_legal_naceTitle')}</Typography>
                    <Grid container>
                      <Grid item xs={12} sm={6}>
                        <FormSelectField
                          name={`naceCode`}
                          label={t('contract_legal_nace')}
                          options={formikProps.values.options.naceCode.map((code) => ({
                            value: code,
                            label: t(`nace_${code}`),
                          }))}
                        />
                      </Grid>
                    </Grid>
                  </Background>

                  <TaxResidency readOnly={readOnly} formikProps={formikProps} lockCountry={!detailMode} />

                  {!detailMode && (
                    <Background inside>
                      <BankAccounts
                        accountId={accountId}
                        rootStore={rootStore}
                        readOnly={readOnly}
                        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
                        errors={globalErrToForm(formikProps.errors.bankData)}
                        legal
                      />
                      <Grid container>
                        <Grid item xs={12}>
                          <Switch
                            name={`bankAccountsConfirmed`}
                            label={t('bankAccount_confirmed')}
                            tooltipContent={t('tooltip_bankAccount_confirmed')}
                            disabled={readOnly}
                          />
                        </Grid>
                      </Grid>
                    </Background>
                  )}
                </>
              )}

              <Background compressed={legal} inside>
                <Typography variant="h3">
                  {t(legal ? 'contract_legal_representative' : 'contract_basicData')}
                </Typography>

                {legal && !readOnly && (
                  <Grid container>
                    <ClientSearch
                      accountId={accountId}
                      personType={PersonType.PHYSICAL}
                      refresh={() => store.cherrypickRepresentative(formikProps as FormikProps<ContractLegalData>)}
                      representative
                      detailMode={detailMode}
                    />
                  </Grid>
                )}

                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name={`${prefix}firstname`}
                      label={t(legal ? 'contract_representative_name' : 'general_clientName')}
                      readOnly={readOnly || (!detailUnlock && !legal && formikProps.values.readOnly) || false}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name={`${prefix}lastname`}
                      label={t('general_surname')}
                      readOnly={readOnly || (!detailUnlock && !legal && formikProps.values.readOnly) || false}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <FormTextField
                      name={`${prefix}degreePrefix`}
                      label={t('general_degreePrefix')}
                      readOnly={readOnly || (!detailUnlock && !legal && formikProps.values.readOnly) || false}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={4} sm={3}>
                    <FormTextField
                      name={`${prefix}degreeSuffix`}
                      label={t('general_degreeSuffix')}
                      readOnly={readOnly || (!detailUnlock && !legal && formikProps.values.readOnly) || false}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={4} sm={6}>
                    {readOnly || (!legal && !detailUnlock) ? (
                      <FormTextField
                        name={`${prefix}gender`}
                        label={t('general_gender')}
                        value={t(
                          `gender_${
                            'gender' in formikProps.values
                              ? formikProps.values.gender
                              : formikProps.values.representative.gender
                          }`,
                        )}
                        endAdornment={
                          readOnly ? undefined : (
                            <Tooltip title={t('tooltip_personalData_lockedInput')}>
                              <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                            </Tooltip>
                          )
                        }
                        readOnly
                      />
                    ) : (
                      <FormSelectField
                        name={`${prefix}gender`}
                        label={t('general_gender')}
                        options={formikProps.values.options.gender.map((gender) => ({
                          value: gender,
                          label: t(`gender_${gender}`),
                        }))}
                      />
                    )}
                  </Grid>
                  <Grid item xs={8} sm={6}>
                    {readOnly || !legal || (legal && detailUnlock) ? (
                      <FormTextField
                        name={`${prefix}birthDate`}
                        label={t('general_birthDate')}
                        value={new Date(
                          'birthDate' in formikProps.values
                            ? formikProps.values.birthDate
                            : formikProps.values.representative.birthDate,
                        ).toLocaleDateString(i18n.language)}
                        endAdornment={
                          !detailUnlock && !readOnly ? (
                            <Tooltip title={t('tooltip_personalData_lockedInput')}>
                              <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                            </Tooltip>
                          ) : undefined
                        }
                        readOnly
                        fast={false}
                      />
                    ) : (
                      <DatePicker
                        maxDate={DateTime.local().minus({ years: 18 })} // * it still shows now-17 year, but it can't be used
                        minDate={DateTime.local(1900, 1, 1)}
                        name={`${prefix}birthDate`}
                        label={t('general_birthDate')}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name={`${prefix}placeOfBirth`}
                      label={t('general_placeOfBirth_city')}
                      readOnly={readOnly}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {readOnly ? (
                      <FormTextField
                        name={`${prefix}countryOfBirth`}
                        label={t('general_placeOfBirth_country')}
                        value={t(
                          `country_${
                            'countryOfBirth' in formikProps.values
                              ? formikProps.values.countryOfBirth
                              : formikProps.values.representative.countryOfBirth
                          }`,
                        )}
                        readOnly
                      />
                    ) : (
                      <FormSelectField
                        name={`${prefix}countryOfBirth`}
                        label={t('general_placeOfBirth_country')}
                        options={sortCountries(
                          formikProps.values.options.residencyBirthCountry.map((country) => ({
                            value: country,
                            label: t(`country_${country}`),
                          })),
                        )}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {readOnly ? (
                      <FormTextField
                        name={`${prefix}citizenship`}
                        label={t('general_citizenship')}
                        value={t(
                          `country_${
                            'citizenship' in formikProps.values
                              ? formikProps.values.citizenship
                              : formikProps.values.representative.citizenship
                          }`,
                        )}
                        readOnly
                      />
                    ) : (
                      <FormSelectField
                        name={`${prefix}citizenship`}
                        label={t('general_citizenship')}
                        options={sortCountries(
                          formikProps.values.options.citizenship.map((country) => ({
                            value: country,
                            label: t(`country_${country}`),
                          })),
                        )}
                        afterChange={() => {
                          store.getForeignIdType(formikProps.values, legal, formikProps);
                          allowIdTypes(
                            'representative' in formikProps.values
                              ? formikProps.values.representative.identificationDocument.type
                              : formikProps.values.identificationDocument.type,
                            formikProps.values,
                            formikProps,
                          );
                        }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={8} sm={6}>
                    <PersonalId
                      extData={!legal && formikProps.values.readOnly}
                      readOnly={readOnly}
                      detailUnlock={detailUnlock}
                      prefix={prefix}
                      foreignIdType={
                        'representative' in formikProps.values
                          ? formikProps.values.representative.foreignIdType
                          : formikProps.values.foreignIdType
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name={`${prefix}email`}
                      label={t('general_email')}
                      readOnly={readOnly || (!detailUnlock && (!legal || formikProps.values.readOnly)) || false}
                      endAdornment={
                        !legal && !detailUnlock && !readOnly ? (
                          <Tooltip title={t('tooltip_personalData_lockedInputAlt')}>
                            <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                          </Tooltip>
                        ) : undefined
                      }
                      fast={false}
                      specificError
                    />
                  </Grid>
                  <Grid item xs={8} sm={6}>
                    {readOnly || (!detailUnlock && (!legal || formikProps.values.readOnly)) ? (
                      <FormTextField
                        name={`${prefix}phone`}
                        label={t('general_phone')}
                        readOnly
                        endAdornment={
                          !legal && !detailUnlock && !readOnly ? (
                            <Tooltip title={t('tooltip_personalData_lockedInputAlt')}>
                              <LockOutlinedIcon fontSize="small" style={{ color: palette.text.disabled }} />
                            </Tooltip>
                          ) : undefined
                        }
                      />
                    ) : (
                      <FormPhoneField name={`${prefix}phone`} label={t('general_phone')} />
                    )}
                  </Grid>
                  {detailUnlock && (
                    <Grid item xs={12}>
                      <ErrorIcon
                        fontSize="medium"
                        style={{
                          color: palette.primary.light,
                          verticalAlign: 'bottom',
                          marginRight: '8px',
                        }}
                      />
                      <strong>
                        <em
                          dangerouslySetInnerHTML={{
                            __html: t('accountDetail_personalData_contactInfo', {
                              interpolation: { escapeValue: false },
                            }),
                          }}
                        />
                      </strong>
                    </Grid>
                  )}
                </Grid>
              </Background>

              <Background compressed={legal} inside>
                <Typography variant="h3">
                  {t('contract_permanentAddress')}
                  {legal && t('contract_representative')}
                </Typography>
                <Address
                  readOnly={readOnly}
                  prefix={prefix}
                  addressType="permanentAddress"
                  countries={formikProps.values.options?.residencyBirthCountry}
                  country={formikProps.values.permanentAddress.country}
                  handleCitizenshipChange={() =>
                    allowIdTypes(
                      'representative' in formikProps.values
                        ? formikProps.values.representative.identificationDocument.type
                        : formikProps.values.identificationDocument.type,
                      formikProps.values,
                      formikProps,
                    )
                  }
                />
              </Background>

              <Background compressed={legal} inside>
                <Grid container>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="h3">
                      {t('contract_contactAddress')}
                      {legal && t('contract_representative')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Switch
                      name={`${prefix}contactAndPermanentAddressesAreSame`}
                      label={t(`contract_contactAddress_same${mobile ? '_short' : ''}`)}
                      disabled={readOnly}
                    />
                  </Grid>
                </Grid>
                {((!legal && !formikProps.values.contactAndPermanentAddressesAreSame) ||
                  ('representative' in formikProps.values &&
                    !formikProps.values.representative.contactAndPermanentAddressesAreSame)) && (
                  <Address
                    readOnly={readOnly}
                    prefix={prefix}
                    addressType="contactAddress"
                    countries={formikProps.values.options?.countryCode}
                    country={formikProps.values.permanentAddress.country}
                  />
                )}
              </Background>

              <Background compressed={legal} inside>
                <Typography variant="h3">{t(`contract_clientId${legal ? '_representative' : ''}`)}</Typography>
                {readOnly ? (
                  <Grid container>
                    <Grid item xs={12}>
                      <Box mb={1} mt={2}>
                        <Typography>
                          {t('contract_clientId_type')}:{' '}
                          {t(
                            `general_idType_${
                              'identificationDocument' in formikProps.values
                                ? formikProps.values.identificationDocument.type
                                : formikProps.values.representative.identificationDocument.type
                            }`,
                          )}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <FormRadioGroup
                    name={`${prefix}identificationDocument.type`}
                    label={t('contract_clientId_type')}
                    formikProps={formikProps as FormikProps<ContractPhysicalData>}
                    values={formikProps.values.options.identificationDocumentType.map((value) => ({
                      value: value,
                      label: t(`general_idType_${value}`),
                    }))}
                    disabledAnswers={
                      permitNeeded(formikProps.values) ? ['NATIONAL_ID', 'PASSPORT'] : ['RESIDENCE_PERMIT']
                    }
                    noMargin
                    fast={false}
                  />
                )}
                <Grid container>
                  <Grid item xs={permitType(formikProps.values) ? 6 : 12} sm={permitType(formikProps.values) ? 3 : 4}>
                    <FormTextField
                      name={`${prefix}identificationDocument.number`}
                      label={t('contract_clientId_number')}
                      onBlur={
                        !readOnly
                          ? () => {
                              validatePassport(formikProps);
                            }
                          : undefined
                      }
                      readOnly={readOnly}
                      fast={false}
                      maxLength={20}
                    />
                  </Grid>
                  {permitType(formikProps.values) && (
                    <Grid item xs={6} sm={3}>
                      <FormSelectField
                        name={`${prefix}identificationDocument.residencePermitType`}
                        label={t('general_residencePermitType')}
                        options={formikProps.values.options.residencePermitType.map((code) => ({
                          value: code,
                          label: t(`general_residencePermitType_${code}`),
                        }))}
                        readOnly={readOnly}
                        fast={false}
                      />
                    </Grid>
                  )}
                  <Grid item xs={6} sm={permitType(formikProps.values) ? 3 : 4}>
                    <DatePicker
                      minDate={DateTime.local(1900, 1, 1)}
                      name={`${prefix}identificationDocument.validFrom`}
                      label={t('contract_clientId_validFrom')}
                      onChange={(date: DateTime | null) => {
                        if (date && date.isValid) {
                          const dateTo = date.plus({ year: 10 });
                          formikProps.setFieldValue(`${prefix}identificationDocument.validTo`, dateTo.toSQLDate());
                          validateFromToDocument(dateTo, formikProps, date);
                        }
                      }}
                      readOnly={readOnly}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={6} sm={permitType(formikProps.values) ? 3 : 4}>
                    <DatePicker
                      minDate={DateTime.local()}
                      name={`${prefix}identificationDocument.validTo`}
                      label={t('general_validTo')}
                      readOnly={readOnly}
                      onChange={(date: DateTime | null) => {
                        validateFromToDocument(date, formikProps);
                      }}
                      fast={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormSelectField
                      name={`${prefix}identificationDocument.issuingCountry`}
                      label={t('contract_clientId_issuingCountry')}
                      options={sortCountries(
                        formikProps.values.options.countryCode.map((code) => ({
                          value: code,
                          label: t(`country_${code}`),
                        })),
                      )}
                      readOnly={readOnly}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormTextField
                      name={`${prefix}identificationDocument.issuer`}
                      label={t('contract_clientId_issuer')}
                      readOnly={readOnly}
                      fast={false}
                    />
                  </Grid>
                  {permitNeeded(formikProps.values) && (
                    <RequiredDocUpload
                      accountId={accountId}
                      docTitle={`contract_personalData_idPermit_tooltip`}
                      docName="identificationDocument.residencePermit"
                      endpoint={
                        detailMode ? API_ACCOUNT_DETAIL_PERSONAL_DATA_PERMIT : API_ACCOUNT_CONTRACT_PERSONAL_DATA_PERMIT
                      }
                      beFile={
                        'representative' in store.data!
                          ? store.data?.representative.identificationDocument.residencePermit
                          : store.data?.identificationDocument.residencePermit
                      }
                      formikProps={formikProps}
                      disabled={readOnly}
                    />
                  )}
                </Grid>
              </Background>

              {!legal && (
                <>
                  <TaxResidency readOnly={readOnly} formikProps={formikProps} mode={mode} lockCountry={!detailMode} />

                  {mode === AccountMode.LTIP && store.data?.employerAllowed && (
                    <Background inside>
                      <Employers
                        accountId={accountId}
                        rootStore={rootStore}
                        readOnly={readOnly}
                        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
                        errors={globalErrToForm(formikProps.errors.employerData)}
                      />
                    </Background>
                  )}

                  {!detailMode && (
                    <Background inside>
                      <BankAccounts
                        accountId={accountId}
                        rootStore={rootStore}
                        readOnly={readOnly}
                        viewMode={detailMode ? ViewMode.DETAIL : ViewMode.WF}
                        errors={globalErrToForm(formikProps.errors.bankData)}
                      />
                      <Grid container>
                        <Grid item xs={12}>
                          <Switch
                            name={`bankAccountsConfirmed`}
                            label={t('bankAccount_confirmed')}
                            tooltipContent={t('tooltip_bankAccount_confirmed')}
                            disabled={readOnly}
                            specificError
                          />
                        </Grid>
                      </Grid>
                    </Background>
                  )}
                </>
              )}

              {detailMode && !readOnly && (
                <Background inside>
                  <Grid container>
                    <Grid item xs={8}>
                      <Switch
                        name={`dataUpdateConsent`}
                        label={`${t('accountDetail_dataUpdateConsent')}`}
                        tooltipContent={t('tooltip_accountDetail_dataUpdateConsent')}
                        specificError
                      />
                    </Grid>
                  </Grid>
                </Background>
              )}

              {detailMode && !readOnly && (
                <ControlButtons isSubmitting={formikProps.isSubmitting} nextText={t('general_continue')} hideBack />
              )}
            </Background>

            {!detailMode && (
              <ControlButtons
                isSubmitting={formikProps.isSubmitting}
                nextText={t('contract_aml')}
                onBack={() => store.handleBack()}
                backText={t('accountStepper_fees')}
              />
            )}
          </Form>
        )}
      </Formik>

      {detailMode && <Notification store={store} ltip={mode === AccountMode.LTIP} />}

      <EditFormModal {...editFormModal} />
    </Container>
  ) : null;
};

export default compose(inject('rootStore'), observer)(PersonalData);
